import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@radix-ui/react-tooltip";
import Button from "./Button";
import Checkbox from "./Checkbox";
import { useApi } from "../";

const DataTable = ({
  data,
  columns,
  actions = [],
  tittle = "Data Table",
  getSelectedRows,
  resetPage,
  showCheckbox = true, // Whether to show the checkbox column at all
  isRowSelectable = () => true, // Function to determine if a row can be selected
  showSelectAll = true, // Whether to show the select all checkbox
  placeholder = "No data available.",
  isLoading = false,
}) => {
  const [selectedRows, setSelectedRows] = useState({});
  const [pageSize, setPageSize] = useState(20);
  const [pageIndex, setPageIndex] = useState(0);
  const { callApi } = useApi();

  // Ensure data is always an array.
  const memoizedData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Calculate total number of pages; default to 1 if no rows.
  const pageCount = useMemo(
    () => Math.ceil(memoizedData.length / pageSize) || 1,
    [memoizedData, pageSize]
  );

  // Reset selection when data changes.
  useEffect(() => {
    setSelectedRows({});
  }, [memoizedData]);

  // Reset pageIndex if resetPage prop changes.
  useEffect(() => {
    setPageIndex(0);
  }, [resetPage]);

  // Reset pageIndex if current page exceeds new pageCount.
  useEffect(() => {
    if (pageIndex >= pageCount) {
      setPageIndex(0);
    }
  }, [pageCount, pageIndex]);

  // Also, whenever data or pageSize changes, ensure pageIndex is valid.
  useEffect(() => {
    setPageIndex((prev) => (prev >= pageCount ? 0 : prev));
  }, [memoizedData, pageSize, pageCount]);

  // Pass selected rows data upward.
  useEffect(() => {
    const selectedData = Object.keys(selectedRows)
      .filter((key) => selectedRows[key]) // Only include truly selected rows
      .map((key) => {
        const index = parseInt(key, 10);
        return memoizedData[index];
      })
      .filter(Boolean); // Filter out any undefined values

    if (typeof getSelectedRows === "function") {
      getSelectedRows(selectedData.reverse());
    }
  }, [selectedRows, memoizedData, getSelectedRows]);

  // Wrap onPaginationChange in useCallback so that pageCount is always current.
  const handlePaginationChange = useCallback(
    (updater) => {
      setPageIndex((prev) => {
        const newPage = typeof updater === "function" ? updater(prev) : updater;
        if (isNaN(newPage) || newPage === undefined) return 0;
        return Math.max(0, Math.min(newPage, pageCount - 1));
      });
    },
    [pageCount]
  );

  const handleExport = async (body) => {
    try {
      console.log(body);
      if (
        body.model === "Admin: Customer Performance" ||
        body.model === "Admin: Courier Performance" ||
        body.model === "Courier Performance" ||
        body.model === "Admin: Export Tracking" ||
        body.model === "Admin: Export Wallet Transaction"
      ) {
        body.ids = [body?.date?.from?.toString(), body?.date?.to?.toString()];
      } else {
        body.ids = data.map((row) => row._id);
      }
      const response = await callApi(
        "/common/export",
        "POST",
        {
          model: body.model,
          ids: body.ids,
          filename: body.filename,
        },
        null,
        null,
        "blob"
      );

      // Check if response is JSON error
      const contentType = response?.headers?.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        console.error("Export failed:", errorData.message);
        return; // Exit early
      }

      // Proceed with blob handling if not JSON
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${body.filename}.csv`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("Export error:", error);
    }
  };
  // Create table instance.
  const table = useReactTable({
    data: memoizedData,
    columns,
    state: { pagination: { pageIndex, pageSize } },
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Get current page rows
  const currentPageRows = table.getRowModel().rows;

  // Memoize the array of selectable row IDs for the current page only
  const selectableRowIds = useMemo(() => {
    return currentPageRows
      .filter((row) => isRowSelectable(row.original, row.index))
      .map((row) => row.index); // Use actual data index instead of row.id
  }, [currentPageRows, isRowSelectable]);

  // Toggle selection for a specific row.
  const toggleRowSelection = (rowIndex, rowData) => {
    // Check if the row is selectable
    if (!isRowSelectable(rowData, rowIndex)) return;

    setSelectedRows((prev) => {
      const newSelection = { ...prev };
      if (newSelection[rowIndex]) {
        delete newSelection[rowIndex]; // Or set to false: newSelection[rowIndex] = false;
      } else {
        newSelection[rowIndex] = true;
      }
      return newSelection;
    });
  };

  // Toggle "Select All" for the current page only.
  const toggleSelectAll = () => {
    if (selectableRowIds.length === 0) return;

    // Check if all selectable rows on the current page are selected
    const allCurrentPageSelected = selectableRowIds.every(
      (rowIndex) => selectedRows[rowIndex]
    );

    if (allCurrentPageSelected) {
      // Unselect all selectable rows on the current page only
      setSelectedRows((prev) => {
        const newSelection = { ...prev };
        selectableRowIds.forEach((rowIndex) => {
          delete newSelection[rowIndex]; // Or set to false: newSelection[rowIndex] = false;
        });
        return newSelection;
      });
    } else {
      // Select all selectable rows on the current page only
      setSelectedRows((prev) => {
        const newSelection = { ...prev };
        selectableRowIds.forEach((rowIndex) => {
          newSelection[rowIndex] = true;
        });
        return newSelection;
      });
    }
  };

  // Function to get tooltip content based on column meta
  const getTooltipContent = (cell) => {
    const column = cell.column.columnDef;
    const row = cell.row.original;

    // Use column's meta.tooltip function if available
    if (column.meta?.tooltip && typeof column.meta.tooltip === "function") {
      return column.meta.tooltip(row) || "";
    }

    // Otherwise render the cell content
    return flexRender(column.cell, cell.getContext());
  };

  // Render pagination buttons.
  const renderPagination = () => {
    const pagesToShow = [];
    const maxPages = 3;
    const startPage = Math.max(1, pageIndex - 1);
    const endPage = Math.min(pageCount, startPage + maxPages - 1);

    if (startPage > 1) pagesToShow.push(1);
    if (startPage > 2) pagesToShow.push("...");

    for (let i = startPage; i <= endPage; i++) {
      pagesToShow.push(i);
    }

    if (endPage < pageCount - 1) pagesToShow.push("...");
    if (endPage < pageCount) pagesToShow.push(pageCount);

    return pagesToShow.map((page, index) =>
      typeof page === "number" ? (
        <button
          key={index}
          className={`p-2 h-10 min-w-10 w-fit ${
            pageIndex + 1 === page
              ? "bg-primary text-secondary/85 border-[1.8px] border-secondary/75 font-semibold"
              : "bg-secondary/90 text-background hover:bg-secondary/80"
          } rounded-lg`}
          onClick={() => setPageIndex(page - 1)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="p-2 mx-1">
          {page}
        </span>
      )
    );
  };

  // If loading, show a skeleton loader styled similarly to the table.
  if (isLoading) {
    return (
      <div className="border-2 border-secondary/20 rounded-lg shadow-lg bg-background_2 overflow-x-auto max-w-full">
        <div className="w-full flex items-center justify-between p-3 border-b-[1.5px] border-secondary/20">
          <div className="h-7 w-40 bg-secondary/20 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-6 w-24 bg-secondary/20 rounded animate-pulse" />
          </div>
        </div>
        <table className="w-full">
          <thead>
            <tr>
              {showCheckbox && (
                <th className="p-2 w-10 border-r-[1.5px] border-b-[1.5px] border-secondary/20 text-center">
                  <div className="h-5 w-5 bg-secondary/20 rounded animate-pulse" />
                </th>
              )}
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className="p-2 border-r-[1.5px] border-b-[1.5px] border-secondary/20 text-center"
                >
                  <div className="h-5 bg-secondary/20 rounded animate-pulse" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array(12)
              .fill(0)
              .map((_, rowIndex) => (
                <tr key={rowIndex}>
                  {showCheckbox && (
                    <td className="p-2 border-b border-r border-secondary/10 text-center">
                      <div className="h-5 w-5 bg-secondary/20 rounded animate-pulse" />
                    </td>
                  )}
                  {columns.map((_, colIndex) => (
                    <td
                      key={colIndex}
                      className="p-2 border-b border-secondary/10 text-center"
                    >
                      <div className="h-6 bg-secondary/20 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={columns.length + (showCheckbox ? 1 : 0)}>
                <div className="flex justify-between items-center p-4">
                  {/* Placeholder for the page info */}
                  <div className="md:px-4 w-32 h-4 bg-secondary/10 rounded animate-pulse" />
                  <div className="flex gap-2">
                    {/* Placeholder for the back button */}
                    <div className="p-2 h-10 min-w-10 bg-secondary/10 rounded animate-pulse" />
                    {/* Placeholder for pagination buttons */}
                    <div className="hidden md:flex gap-2">
                      {Array(3)
                        .fill(0)
                        .map((_, index) => (
                          <div
                            key={index}
                            className="p-2 h-10 min-w-10 bg-secondary/10 rounded animate-pulse"
                          />
                        ))}
                    </div>
                    {/* Placeholder for the forward button */}
                    <div className="p-2 h-10 min-w-10 bg-secondary/10 rounded animate-pulse" />
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  // If there's no data, show the placeholder.
  // if (memoizedData.length === 0) {
  //   return (
  //     <div className="w-full min-h-56 flex justify-center items-center bg-secondary/10 rounded-2xl p-4 text-center text-secondary">
  //       {placeholder}
  //     </div>
  //   );
  // }

  // Calculate if all selectable rows on CURRENT PAGE are selected
  const allCurrentPageRowsSelected =
    selectableRowIds.length > 0 &&
    selectableRowIds.every((rowIndex) => selectedRows[rowIndex]);

  return (
    <TooltipProvider delayDuration={100}>
      <div className="border-2 border-secondary/20 rounded-lg shadow-lg bg-background_2">
        <div className="w-full flex items-center justify-between p-3 border-b-[1.5px] border-secondary/20 font-nunito text-secondary font-semibold text-lg">
          {tittle}
          <div className="flex gap-2">
            {actions.map((action) => (
              <Button
                key={action.name}
                variant="default"
                size="sm"
                icon={action.icon}
                onClick={
                  action.name.includes("Export")
                    ? () => handleExport(action.body)
                    : action.onClick
                }
              >
                {action.name}
              </Button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto max-w-full">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {showCheckbox && (
                    <th className="p-2 !max-w-12 border-r-[1.5px] border-b-[1.5px] border-secondary/20">
                      {showSelectAll && selectableRowIds.length > 0 && (
                        <Checkbox
                          checked={allCurrentPageRowsSelected}
                          onChange={toggleSelectAll}
                        />
                      )}
                    </th>
                  )}
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="p-2 border-r-[1.5px] text-start text-secondary/90 font-nunito text-body2 border-b-[1.5px] border-secondary/20"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {memoizedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={showCheckbox ? columns.length + 1 : columns.length}
                    className="p-4 lg:p-6 text-center text-secondary bg-secondary/5"
                  >
                    <div className="w-full min-h-32 flex justify-center items-center">
                      {placeholder}
                    </div>
                  </td>
                </tr>
              ) : (
                currentPageRows.map((row) => {
                  const rowIndex = row.index;
                  const isSelectable = isRowSelectable(row.original, rowIndex);
                  return (
                    <tr
                      key={row.id}
                      className="hover:bg-background/40 bg-background"
                    >
                      {showCheckbox && (
                        <td className="p-2 border-b mx-auto !w-10 max-w-12 border-r border-secondary/10 text-center">
                          {isSelectable && (
                            <Checkbox
                              checked={!!selectedRows[rowIndex]}
                              onChange={() =>
                                toggleRowSelection(rowIndex, row.original)
                              }
                            />
                          )}
                        </td>
                      )}
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`p-2 px-3 border-b overflow-hidden text-ellipsis text-body2 text-start text-secondary/95 border-secondary/10 cursor-context-menu
                            ${
                              cell.column.columnDef.width
                                ? parseInt(cell.column.columnDef.width) < 112
                                  ? ""
                                  : "min-w-28"
                                : "min-w-28"
                            } max-w-56`}
                          style={
                            cell.column.columnDef.width
                              ? {
                                  width:
                                    cell.column.columnDef.width + " !important",
                                }
                              : undefined
                          }
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate ">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              align="start"
                              className="px-2 py-1 max-w-56 bg-black/90 font-nunito text-background rounded-md border-2 border-background/20 will-change-transform"
                            >
                              {getTooltipContent(cell)}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex justify-between items-center p-4">
          <span className="md:px-4 w-fit font-medium text-secondary/70">
            Page {pageIndex + 1} of {pageCount}
          </span>
          <div className="flex gap-2">
            <select
              className="p-2 h-10 bg-secondary/90 text-background/80 rounded-lg"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageIndex(0);
              }}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <button
              className="p-2 h-10 min-w-10 flex justify-center items-center bg-secondary/90 text-background/90 rounded-lg hover:bg-secondary/80"
              onClick={() => setPageIndex(Math.max(0, pageIndex - 1))}
              disabled={pageIndex === 0}
            >
              <IoIosArrowBack />
            </button>
            <div className="hidden md:flex gap-2">{renderPagination()}</div>
            <button
              className="p-2 h-10 min-w-10 flex justify-center items-center bg-secondary/90 text-background/90 rounded-lg hover:bg-secondary/80"
              onClick={() =>
                setPageIndex(Math.min(pageCount - 1, pageIndex + 1))
              }
              disabled={pageIndex >= pageCount - 1}
            >
              <IoIosArrowForward />
            </button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default DataTable;
